/**
 * PWA Install Prompt Component
 * A dismissible bottom sheet/snackbar that appears above the nav bar
 * Shows on mobile for first 2-3 visits to encourage app installation
 */

import { useState, useEffect } from 'react';
import { AppColors } from '../../theme/colors';
import { usePWAInstall } from '../../hooks/usePWAInstall';
import { useLanguage } from '../../contexts/LanguageContext';

interface PWAInstallPromptProps {
  bottomOffset?: number; // Offset from bottom (for nav bar)
}

export function PWAInstallPrompt({ bottomOffset = 80 }: PWAInstallPromptProps) {
  const { t } = useLanguage();
  const {
    shouldShowPrompt,
    isIOS,
    isInstallable,
    triggerInstall,
    dismissPrompt,
  } = usePWAInstall();

  const [isVisible, setIsVisible] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [showNotAvailable, setShowNotAvailable] = useState(false);

  // Animate in after mount
  useEffect(() => {
    if (shouldShowPrompt) {
      const timer = setTimeout(() => setIsVisible(true), 500);
      return () => clearTimeout(timer);
    }
  }, [shouldShowPrompt]);

  const handleDismiss = () => {
    setIsAnimatingOut(true);
    setTimeout(() => {
      dismissPrompt();
      setIsVisible(false);
      setIsAnimatingOut(false);
    }, 300);
  };

  const handleInstall = async () => {
    if (isIOS) {
      // Show iOS-specific instructions
      setShowIOSInstructions(true);
      return;
    }

    // Check if install is available (beforeinstallprompt fired)
    if (!isInstallable) {
      setShowNotAvailable(true);
      return;
    }

    const result = await triggerInstall();
    if (result.success) {
      handleDismiss();
    }
  };

  if (!shouldShowPrompt && !isVisible) return null;

  // Not Available Modal (for browsers that don't support install prompt)
  if (showNotAvailable) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          backdropFilter: 'blur(4px)',
        }}
        onClick={() => setShowNotAvailable(false)}
      >
        <div
          style={{
            backgroundColor: AppColors.bgSecondary,
            borderRadius: '20px',
            padding: '24px',
            maxWidth: '320px',
            width: '100%',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <h3 style={{
            margin: '0 0 16px 0',
            fontSize: '18px',
            fontWeight: '700',
            color: AppColors.textPrimary,
            textAlign: 'center',
          }}>
            {t('pwa.installNotAvailable')}
          </h3>

          <p style={{
            margin: '0 0 16px 0',
            fontSize: '14px',
            color: AppColors.textSecondary,
            textAlign: 'center',
            lineHeight: 1.5,
          }}>
            {t('pwa.installNotAvailableDesc')} {t('pwa.tryChrome')}
          </p>

          <p style={{
            margin: '0 0 16px 0',
            fontSize: '13px',
            color: AppColors.textMuted,
            textAlign: 'center',
            lineHeight: 1.4,
          }}>
            {t('pwa.bookmarkTip')}
          </p>

          <button
            onClick={() => {
              setShowNotAvailable(false);
              handleDismiss();
            }}
            style={{
              width: '100%',
              padding: '14px',
              borderRadius: '12px',
              border: 'none',
              backgroundColor: AppColors.accent,
              color: '#1e1b4b',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            {t('pwa.gotIt')}
          </button>
        </div>
      </div>
    );
  }

  // iOS Instructions Modal - Enhanced visual version
  if (showIOSInstructions) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px',
          backdropFilter: 'blur(8px)',
        }}
        onClick={() => setShowIOSInstructions(false)}
      >
        <div
          style={{
            backgroundColor: AppColors.bgSecondary,
            borderRadius: '24px',
            padding: '28px 24px',
            maxWidth: '340px',
            width: '100%',
            border: '1px solid rgba(139, 92, 246, 0.2)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header with app icon */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginBottom: '24px',
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '12px',
              boxShadow: '0 8px 16px rgba(139, 92, 246, 0.3)',
            }}>
              <span style={{ fontSize: '24px', fontWeight: '800', color: 'white' }}>AI</span>
            </div>
            <h3 style={{
              margin: 0,
              fontSize: '20px',
              fontWeight: '700',
              color: AppColors.textPrimary,
              textAlign: 'center',
            }}>
              {t('pwa.installTitle')}
            </h3>
            <p style={{
              margin: '6px 0 0 0',
              fontSize: '13px',
              color: AppColors.textMuted,
              textAlign: 'center',
            }}>
              {t('pwa.addToHomeScreen')}
            </p>
          </div>

          {/* Steps */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            marginBottom: '24px',
          }}>
            {/* Step 1 - Safari Share Button */}
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '14px',
              padding: '14px',
              backgroundColor: 'rgba(139, 92, 246, 0.08)',
              borderRadius: '14px',
              border: '1px solid rgba(139, 92, 246, 0.15)',
            }}>
              <div style={{
                width: '44px',
                height: '44px',
                borderRadius: '12px',
                backgroundColor: '#007AFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                {/* Safari Share Icon - accurate iOS style */}
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <rect x="4" y="10" width="16" height="12" rx="2" stroke="white" strokeWidth="2"/>
                  <path d="M12 2v12" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M8 6l4-4 4 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div style={{ flex: 1 }}>
                <p style={{
                  margin: 0,
                  fontSize: '15px',
                  fontWeight: '600',
                  color: AppColors.textPrimary,
                  marginBottom: '2px',
                }}>
                  {t('pwa.tapShare')}
                </p>
                <p style={{
                  margin: 0,
                  fontSize: '13px',
                  color: AppColors.textMuted,
                }}>
                  {t('pwa.findInSafari')}
                </p>
              </div>
            </div>

            {/* Step 2 - Add to Home Screen */}
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '14px',
              padding: '14px',
              backgroundColor: 'rgba(139, 92, 246, 0.08)',
              borderRadius: '14px',
              border: '1px solid rgba(139, 92, 246, 0.15)',
            }}>
              <div style={{
                width: '44px',
                height: '44px',
                borderRadius: '12px',
                backgroundColor: AppColors.bgTertiary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                border: '1px solid rgba(255,255,255,0.1)',
              }}>
                {/* Add to Home Screen Icon */}
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="3" width="18" height="18" rx="4" stroke={AppColors.accent} strokeWidth="2"/>
                  <path d="M12 8v8M8 12h8" stroke={AppColors.accent} strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <div style={{ flex: 1 }}>
                <p style={{
                  margin: 0,
                  fontSize: '15px',
                  fontWeight: '600',
                  color: AppColors.textPrimary,
                  marginBottom: '2px',
                }}>
                  {t('pwa.addToHome')}
                </p>
                <p style={{
                  margin: 0,
                  fontSize: '13px',
                  color: AppColors.textMuted,
                }}>
                  {t('pwa.scrollShareMenu')}
                </p>
              </div>
            </div>

            {/* Step 3 - Confirm */}
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '14px',
              padding: '14px',
              backgroundColor: 'rgba(74, 222, 128, 0.08)',
              borderRadius: '14px',
              border: '1px solid rgba(74, 222, 128, 0.15)',
            }}>
              <div style={{
                width: '44px',
                height: '44px',
                borderRadius: '12px',
                backgroundColor: 'rgba(74, 222, 128, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                {/* Checkmark Icon */}
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path d="M5 13l4 4L19 7" stroke={AppColors.success} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div style={{ flex: 1 }}>
                <p style={{
                  margin: 0,
                  fontSize: '15px',
                  fontWeight: '600',
                  color: AppColors.textPrimary,
                  marginBottom: '2px',
                }}>
                  {t('pwa.tapAdd')}
                </p>
                <p style={{
                  margin: 0,
                  fontSize: '13px',
                  color: AppColors.textMuted,
                }}>
                  {t('pwa.appWillAppear')}
                </p>
              </div>
            </div>
          </div>

          <button
            onClick={() => {
              setShowIOSInstructions(false);
              handleDismiss();
            }}
            style={{
              width: '100%',
              padding: '16px',
              borderRadius: '14px',
              border: 'none',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
              color: 'white',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(139, 92, 246, 0.4)',
            }}
          >
            {t('pwa.gotIt')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes slideUpPWA {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        @keyframes slideDownPWA {
          from {
            transform: translateY(0);
            opacity: 1;
          }
          to {
            transform: translateY(100%);
            opacity: 0;
          }
        }
      `}</style>

      <div
        style={{
          position: 'fixed',
          left: '12px',
          right: '12px',
          bottom: `calc(${bottomOffset}px + env(safe-area-inset-bottom))`,
          zIndex: 50,
          animation: isAnimatingOut
            ? 'slideDownPWA 0.3s ease-out forwards'
            : 'slideUpPWA 0.4s ease-out forwards',
        }}
      >
        <div
          style={{
            backgroundColor: 'rgba(30, 27, 75, 0.95)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderRadius: '16px',
            padding: '16px',
            border: '1px solid rgba(139, 92, 246, 0.3)',
            boxShadow: '0 -4px 24px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(139, 92, 246, 0.1)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          {/* App Icon */}
          <div
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: '0 2px 8px rgba(139, 92, 246, 0.3)',
            }}
          >
            <span style={{
              fontSize: '18px',
              fontWeight: '800',
              color: 'white',
            }}>AI</span>
          </div>

          {/* Text */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{
              margin: 0,
              fontSize: '14px',
              fontWeight: '600',
              color: AppColors.textPrimary,
              lineHeight: 1.3,
            }}>
              {t('pwa.installApp')}
            </p>
            <p style={{
              margin: '2px 0 0 0',
              fontSize: '12px',
              color: AppColors.textSecondary,
              lineHeight: 1.3,
            }}>
              {t('pwa.fasterAccess')}
            </p>
          </div>

          {/* Install Button */}
          <button
            onClick={handleInstall}
            style={{
              padding: '10px 16px',
              borderRadius: '10px',
              border: 'none',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
              color: 'white',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              flexShrink: 0,
              boxShadow: '0 2px 8px rgba(139, 92, 246, 0.4)',
            }}
          >
            {isIOS ? t('pwa.how') : t('pwa.install')}
          </button>

          {/* Dismiss Button */}
          <button
            onClick={handleDismiss}
            style={{
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              border: 'none',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: AppColors.textSecondary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              flexShrink: 0,
            }}
            aria-label="Dismiss"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>
    </>
  );
}

export default PWAInstallPrompt;
